import React, { useState, useEffect } from 'react';
import { api } from '../api/client.ts';
import { useGame } from '../state/gameStore.tsx';
import type { BoostInventoryItem } from '@galaxy/shared';
import { telegramHaptics } from '../telegram/sdk.ts';

interface QuickBoostModalProps {
  targetType: 'building' | 'research' | 'shipyard';
  targetId: string;
  targetName: string;
  remainingSeconds: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const QuickBoostModal: React.FC<QuickBoostModalProps> = ({
  targetType,
  targetId,
  targetName,
  remainingSeconds,
  onClose,
  onSuccess,
}) => {
  const { setNotification, refreshState } = useGame();
  const [inventory, setInventory] = useState<BoostInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getBoostInventory()
      .then((res) => {
        if (res.success) {
          // Filter items applicable to this target or 'all'
          const applicable = res.items.filter(
            (i) => i.target_type === targetType || i.target_type === 'all'
          );
          setInventory(applicable);
          if (applicable.length > 0) {
            setSelectedItemId(applicable[0].id);
          }
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [targetType]);

  const handleApply = async () => {
    if (!selectedItemId) return;
    try {
      setSubmitting(true);
      telegramHaptics.impact('heavy');
      const res = await api.consumeBoost(selectedItemId, targetType, targetId);
      if (res.success) {
        telegramHaptics.notification('success');
        const minSaved = Math.round(res.time_saved_ms / 60000);
        setNotification(`⚡ Đã tăng tốc ${targetName}! Tiết kiệm ${minSaved} phút.`);
        await refreshState();
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      telegramHaptics.notification('error');
      setNotification(`⚠️ ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      zIndex: 2500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      backdropFilter: 'blur(6px)',
    }}>
      <div style={{
        background: 'var(--bg-panel)',
        border: '1px solid var(--accent-cyan)',
        borderRadius: 8,
        padding: 18,
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 0 25px rgba(0, 229, 255, 0.3)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          borderBottom: '1px solid var(--border-panel)',
          paddingBottom: 8,
        }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent-cyan)' }}>
            ⚡ TĂNG TỐC TIẾN TRÌNH
          </span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: 16, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-bright)' }}>
            {targetName}
          </div>
          <div style={{ fontSize: 11, color: 'var(--accent-amber)', marginTop: 2 }}>
            ⏳ Thời gian còn lại: ~{Math.ceil(remainingSeconds / 60)} phút ({remainingSeconds}s)
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-dim)', fontSize: 12 }}>
            Đang tải kho vật phẩm tăng tốc...
          </div>
        ) : inventory.length === 0 ? (
          <div style={{
            padding: 14,
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px dashed var(--border-panel)',
            borderRadius: 6,
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--text-dim)',
            marginBottom: 14,
          }}>
            Không có thẻ tăng tốc phù hợp trong kho! Hãy vào tab <strong>THƯỞNG</strong> để điểm danh hoặc <strong>SHOP</strong> để mua thêm.
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>
              CHỌN THẺ TĂNG TỐC TRONG KHO:
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {inventory.map((item) => (
                <label
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: selectedItemId === item.id ? '1px solid var(--accent-cyan)' : '1px solid var(--border-panel)',
                    background: selectedItemId === item.id ? 'rgba(0, 229, 255, 0.12)' : 'rgba(10, 17, 36, 0.5)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="boostItem"
                    value={item.id}
                    checked={selectedItemId === item.id}
                    onChange={() => setSelectedItemId(item.id)}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-bright)' }}>
                        {item.name}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent-cyan)' }}>
                        x{item.quantity}
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>
                      {item.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            className="sci-btn"
            onClick={onClose}
            style={{ padding: '8px 14px', fontSize: 11, background: 'transparent' }}
          >
            ĐÓNG
          </button>
          <button
            className="sci-btn"
            disabled={inventory.length === 0 || !selectedItemId || submitting}
            onClick={handleApply}
            style={{
              padding: '8px 16px',
              fontSize: 11,
              fontWeight: 800,
              background: 'linear-gradient(135deg, var(--accent-cyan) 0%, #0284c7 100%)',
              color: '#050811',
            }}
          >
            {submitting ? 'ĐANG ÁP DỤNG...' : '⚡ XÁC NHẬN'}
          </button>
        </div>
      </div>
    </div>
  );
};
